/**
 * Copyright 2023 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { useRef } from 'react';
import { useVisualFilters } from '@/hooks/useVisualFilters';
import { useProductList } from '@/features/shelf/context/useProductList';
import { useProductShelfResults } from '@/features/category/api/getProductShelfResults';
import { useProductShelfACESResults } from '@/features/category/api/getProductShelfACESResults';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useFilterAndRedirect } from '@/hooks/useFilterAndRedirect';
import { Actionable, Card, Loader, Text, View } from '@az/starc-ui';
import { comingSoonImg } from '@/constants/images';
import styles from './styles.module.scss';
import NextImage from '@/components/NextImage';
import type { ProductShelfResultsModel, VisualFilterModel } from '@/api/types/browse-search-types';
import type { HandleFilterType, FilterDataType } from '@/types/filterTypes';
import type { MutableRefObject, RefObject } from 'react';
import { CarouselButton, useBuildACarousel } from './Carousel';
import { useAcesFlags } from '@/features/ymme/utils/acesFlags';

const VisualFilterCard = ({
  visualFilter,
  onClick,
}: {
  visualFilter: HandleFilterType;
  onClick: () => void;
}) => {
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const isDesktop = useMediaQuery((theme) => theme.breakpoints.up('lg'));
  const image = { height: isMobile ? 55 : 70, width: isMobile ? 55 : 70 };

  return (
    <Actionable onClick={onClick} className={styles.visualFilterCardContainer}>
      <Card className={styles.card} hasHoverEffect={isDesktop} width="112px">
        <View align="center" padding={1} direction="column">
          <View.Item>
            <NextImage
              width={image.width}
              height={image.height}
              src={visualFilter?.imageUrl ?? comingSoonImg.src}
              alt={`${visualFilter?.displayName} ${visualFilter?.refinement?.label}`}
              className={styles.scalableImageOptimization}
            />
          </View.Item>
          <View.Item gapBefore={1}>
            <View width="104px">
              <Text className={styles.cardText}>{visualFilter?.refinement?.label}</Text>
            </View>
          </View.Item>
        </View>
      </Card>
    </Actionable>
  );
};

export const VisualFilters = () => {
  const isDesktop = useMediaQuery((theme) => theme.breakpoints.up('lg'));
  const visualFiltersContainerRef = useRef<HTMLDivElement | null>(null);
  const windowWidth =
    visualFiltersContainerRef?.current?.parentElement?.getBoundingClientRect().width || 0;
  const { acesEnabled } = useAcesFlags();

  const handleFilterAndRedirect = useFilterAndRedirect();
  const { data: productListData, isPending: isProductListPending } = useProductList();
  const useShelfResultsFunction = acesEnabled ? useProductShelfACESResults : useProductShelfResults;
  const { data: productShelvesResultsData, isPending: isProductShelfResultsPending } =
    useShelfResultsFunction();

  const staticNavigation =
    productListData?.shelfLeftNav?.[0]?.staticNavigation || new Array<FilterDataType>();
  const productShelvesResultsVisualFilters = !acesEnabled
    ? ((productShelvesResultsData?.productShelfResults as ProductShelfResultsModel)
        ?.visualFilters as VisualFilterModel[]) || new Array<VisualFilterModel>()
    : new Array<VisualFilterModel>();

  const visualFilters = useVisualFilters(productShelvesResultsVisualFilters, staticNavigation);
  const isLoadingState =
    !(productListData || productShelvesResultsData) &&
    (isProductListPending || isProductShelfResultsPending);

  const lineItemRefs = useRef<Array<MutableRefObject<HTMLLIElement>>>([]);
  const scrollableContainerRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement | null>(
    null,
  );

  const { swipeableHandlers, hideNextButton, hidePreviousButton, handleScrollTo } =
    useBuildACarousel({
      arrayLength: visualFilters?.length,
      lineItemRefs,
      scrollableContainerRef,
    });

  if (!isLoadingState && visualFilters?.length < 2) {
    return null;
  }

  return (
    <div ref={visualFiltersContainerRef} className={styles.visualFiltersContainer}>
      <View
        width={`${windowWidth}px`}
        padding={{ s: [0, 0, 0, 4], m: [0, 0, 2, 4], l: [0, 0, 5, 0] }}
      >
        {isLoadingState ? (
          <View.Item grow attributes={{ 'data-testid': 'visual-filters-loader' }}>
            <Loader variant="spinner" />
          </View.Item>
        ) : (
          <>
            {isDesktop && (
              <View attributes={{ style: { position: 'absolute' } }}>
                <CarouselButton
                  direction="previous"
                  handleScroll={() => handleScrollTo('previous')}
                  hideButton={hidePreviousButton}
                />
              </View>
            )}
            <div
              {...swipeableHandlers}
              ref={scrollableContainerRef}
              className={styles.visualFiltersScrollableContainer}
              role="ul"
            >
              {visualFilters.map((visualFilter: HandleFilterType, index) => (
                <li
                  key={`${visualFilter?.dimensionName}${index}`}
                  ref={lineItemRefs.current[index]}
                  data-testid={`visual-filter-${index}`}
                >
                  <VisualFilterCard
                    onClick={() => {
                      // TODO - Pass effect (analytics) for PDB-542
                      handleFilterAndRedirect(visualFilter, () => null);
                    }}
                    visualFilter={visualFilter}
                  />
                </li>
              ))}
            </div>
            {isDesktop && (
              <View attributes={{ style: { position: 'absolute' } }} width={`${windowWidth}px`}>
                <CarouselButton
                  direction="next"
                  handleScroll={() => handleScrollTo('next')}
                  hideButton={hideNextButton}
                />
              </View>
            )}
          </>
        )}
      </View>
    </div>
  );
};
